import { Container, Sprite, Text, Texture } from 'pixi.js';
import type { RoadLandmark } from '@goodtime/shared';
import { landmarkAlias, landmarkName, roadPurpose } from '../../logic/road/landmarks';
import { BoardDialog, BrassButton, COLOURS, layoutButtonRow, uiStyle } from '../board/index';
import { balancedWrapWidth } from './balancedWrap';

const coins = (n: number) => n.toLocaleString('en-GB');

/** What came of asking the server to restore. */
export type RestoreOutcome =
  /** `opened`: finishing the chapter opened its next slot just now. */
  | { kind: 'restored'; charged: number; chapterComplete: boolean; opened: boolean }
  | { kind: 'short'; coins: number }
  | { kind: 'failed' };

export interface RestoreCardOptions {
  landmarks: readonly RoadLandmark[];
  index: number;
  /** The player's coins, as the game last heard. */
  coins: number;
  /** The slot finishing the chapter opens, by name (null for none). */
  next: string | null;
  /** The player already has that slot, so the card doesn't promise it. */
  nextOwned?: boolean;
  restore: () => Promise<RestoreOutcome>;
  /** It restored; the card has closed. */
  onRestored: (outcome: Extract<RestoreOutcome, { kind: 'restored' }>) => void;
}

/**
 * A ruined landmark's card, over the slot: its painting, what the road is
 * for, and what restoring this one costs. The next landmark restores here
 * when the player can afford it; otherwise the card says how far off it is,
 * or which landmark comes first.
 */
export class RestoreDialog extends Container {
  private dialog: BoardDialog | null = null;
  private viewW = 1;
  private viewH = 1;
  /** The server's word on the wallet, when it disagreed with ours. */
  private serverCoins: number | null = null;

  constructor(
    private readonly texture: (alias: string) => Texture | null,
    private readonly floor: () => Texture | null,
  ) {
    super();
  }

  get isOpen(): boolean {
    return this.dialog !== null;
  }

  open(opts: RestoreCardOptions): void {
    this.serverCoins = null;
    this.show(opts, null, false, true);
  }

  close(): void {
    const dialog = this.dialog;
    this.dialog = null;
    if (!dialog) return;
    void dialog.close().then(() => dialog.destroy({ children: true }));
  }

  layout(w: number, h: number): void {
    this.viewW = w;
    this.viewH = h;
    this.dialog?.layout(w, h);
  }

  /** Draw the card for its current state (rebuilt on each change). */
  private show(
    opts: RestoreCardOptions,
    error: string | null,
    busy: boolean,
    animateIn = false,
  ): void {
    const old = this.dialog;
    const { landmarks, index } = opts;
    const landmark = landmarks[index]!;
    const next = landmarks.findIndex((l) => !l.restored);
    const have = this.serverCoins ?? opts.coins;

    const dialog = new BoardDialog({
      title: landmarkName(landmark.id).toUpperCase(),
      floor: this.floor(),
    });
    dialog.layout(this.viewW, this.viewH);
    const inner = dialog.contentWidth;
    const body = dialog.content;
    let y = 0;

    const tex = this.texture(landmarkAlias(landmark.id, false));
    const side = Math.max(96, Math.min(150, Math.round(this.viewH * 0.2)));
    if (tex) {
      const art = new Sprite(tex);
      art.anchor.set(0.5, 0);
      art.scale.set(side / Math.max(tex.width, tex.height));
      art.position.set(inner / 2, y);
      body.addChild(art);
    }
    y += side + 6;

    const say = (
      text: string,
      size: number,
      weight: '500' | '600' | '700',
      fill: number = COLOURS.cream,
    ) => {
      const style = {
        ...uiStyle(size, fill, weight),
        align: 'center' as const,
        lineHeight: Math.round(size * 1.3),
      };
      const t = new Text({
        text,
        style: { ...style, wordWrap: true, wordWrapWidth: balancedWrapWidth(text, style, inner) },
      });
      t.anchor.set(0.5, 0);
      t.position.set(inner / 2, y);
      body.addChild(t);
      y += t.height + 8;
    };
    say(roadPurpose(landmarks.length - 1, opts.next, opts.nextOwned), 14, '500');

    const close = () => this.close();
    let cancel: BrassButton | null = null;
    let action: BrassButton;
    if (index !== next) {
      say(`Restore the ${landmarkName(landmarks[next]!.id)} first.`, 17, '700', 0xffffff);
      action = new BrassButton({ label: 'OK', kind: 'gold', onTap: close });
    } else if (have < landmark.cost) {
      say(
        `Restoring it takes ${coins(landmark.cost)} coins: ${coins(landmark.cost - have)} more to go.`,
        17,
        '700',
        0xffffff,
      );
      action = new BrassButton({ label: 'KEEP PLAYING', kind: 'gold', onTap: close });
    } else {
      say(`Restore it for ${coins(landmark.cost)} coins?`, 17, '700', 0xffffff);
      cancel = new BrassButton({ label: 'Not yet', kind: 'wood', onTap: close });
      action = new BrassButton({
        label: busy ? 'RESTORING…' : 'RESTORE',
        kind: 'gold',
        onTap: () => void this.restore(opts),
      });
      if (busy) {
        cancel.setEnabled(false);
        action.setEnabled(false);
      }
    }
    y += 4;
    if (cancel) body.addChild(cancel);
    body.addChild(action);
    layoutButtonRow(cancel, action, 0, y, inner);
    y += action.buttonHeight + 3;
    if (error) {
      y += 8;
      say(error, 13, '600', COLOURS.loss);
    }
    dialog.setContentHeight(y);

    this.addChild(dialog);
    this.dialog = dialog;
    if (animateIn) void dialog.open();
    else dialog.visible = true;
    old?.destroy({ children: true });
  }

  private async restore(opts: RestoreCardOptions): Promise<void> {
    this.show(opts, null, true);
    const outcome = await opts.restore();
    if (!this.dialog) return;
    if (outcome.kind === 'restored') {
      this.close();
      opts.onRestored(outcome);
    } else if (outcome.kind === 'short') {
      this.serverCoins = outcome.coins;
      this.show(opts, null, false);
    } else {
      this.show(opts, 'Couldn’t restore it just now. Try again.', false);
    }
  }
}
